import { ReactQuestionFactory } from "survey-react-ui";
import { Select } from "../ui";
import {
  ChangeEvent,
  createElement,
  useState,
  useEffect,
  useMemo,
} from "react";
import { QuestionTagboxModel, RendererFactory } from "survey-core";
import Textarea from "../ui/textarea";
import {
  mergeOptionsWithStorage,
  saveOptionToStorage,
  createStorageEventHandler,
  createCustomEventHandler,
  normalizeQuestionChoices,
  createSelectFilterFunction,
} from "../../utils/select-utils";
import { Option } from "../../types/select";

interface MultiselectQuestionProps {
  question: QuestionTagboxModel;
  isDisplayMode: boolean;
}

const MultiselectQuestion: React.FC<MultiselectQuestionProps> = ({
  question,
  isDisplayMode,
}) => {
  const localStorageKey = question.getPropertyValue(
    "localStorageKey",
    question.getValueName()
  );

  // State management
  const [comment, setComment] = useState(question.comment);
  const [selectedValues, setSelectedValues] = useState<string[]>(
    Array.isArray(question.value) ? (question.value as string[]) : []
  );
  const [options, setOptions] = useState(() => {
    const schemaOptions = normalizeQuestionChoices(question.visibleChoices);
    return mergeOptionsWithStorage(schemaOptions, localStorageKey);
  });

  // Computed values using useMemo
  const currentOptions = useMemo(() => {
    const schemaOptions = normalizeQuestionChoices(question.visibleChoices);
    return mergeOptionsWithStorage(schemaOptions, localStorageKey);
  }, [localStorageKey, question.visibleChoices]);

  const defaultValue = useMemo(() => {
    if (!question.defaultValue) return undefined;
    return options.filter((o: Option) =>
      question.defaultValue.includes(o.value)
    );
  }, [question.defaultValue, options]);

  const selectedOption = useMemo(() => {
    const currentValue = selectedValues || [];
    return options.filter((o: Option) => currentValue.includes(o.value));
  }, [selectedValues, options]);

  // Methods converted to functions
  const noOptionsMessage = () => {
    return question.getPropertyValue(
      "noOptionsMessage",
      "%-%tolgee:select-question-no-options-default-message%-%"
    );
  };

  const onSelectChange = (selectedOptions: any) => {
    const values = Array.isArray(selectedOptions)
      ? selectedOptions.map((o: any) => o.value)
      : [];
    setSelectedValues(values);
    question.value = values;
  };

  const onCommentChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
    setComment(event.target.value);
  };

  const onCommentBlur = () => {
    question.comment = comment;
  };

  const onCreateOption = (inputValue: string) => {
    const newOption = [{ value: inputValue, label: inputValue }];

    // Persist in localStorage and notify other components
    saveOptionToStorage(inputValue, localStorageKey);

    onSelectChange([...selectedOption, ...newOption]);
  };

  const formatCreateLabel = (inputValue: string) => {
    return question.getPropertyValue(
      "createOptionMessage",
      `%-%tolgee:select-question-create-option-default-message:input:${inputValue}%-%`
    );
  };

  // // Listen to cross-tab localStorage updates for this storageKey
  useEffect(() => {
    if (!localStorageKey || typeof window === "undefined") return;

    const schemaOptions = normalizeQuestionChoices(question.visibleChoices);

    const handleStorage = createStorageEventHandler(
      localStorageKey,
      schemaOptions,
      setOptions
    );

    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, [localStorageKey, question]);

  // // Listen for same-tab updates via a custom event and refresh options
  useEffect(() => {
    if (!localStorageKey || typeof window === "undefined") return;

    const schemaOptions = normalizeQuestionChoices(question.visibleChoices);

    const handleCustom = createCustomEventHandler(
      localStorageKey,
      schemaOptions,
      setOptions
    );

    window.addEventListener("m2f:select-options-updated", handleCustom as any);
    return () =>
      window.removeEventListener(
        "m2f:select-options-updated",
        handleCustom as any
      );
  }, [localStorageKey, question]);

  // Update options when currentOptions change
  useEffect(() => {
    setOptions(currentOptions);
  }, [currentOptions]);

  // Sync local selectedValues state when question.value changes externally
  useEffect(() => {
    const current = Array.isArray(question.value)
      ? (question.value as string[])
      : [];
    setSelectedValues(current);
  }, [question.value]);

  return (
    <>
      <Select
        isMulti
        isCreatable={question.allowCustomChoices}
        isDisabled={question.isReadOnly || isDisplayMode}
        id={question.inputId}
        defaultValue={defaultValue}
        value={selectedOption}
        onChange={onSelectChange}
        options={options}
        onCreateOption={onCreateOption}
        filterOption={createSelectFilterFunction(options)}
        required={question.isRequired}
        menuPortalTarget={
          typeof document !== "undefined"
            ? document.querySelector("body")
            : undefined
        }
        placeholder={question.placeholder}
        isClearable={question.allowClear}
        isSearchable={question.searchEnabled}
        noOptionsMessage={noOptionsMessage}
        closeMenuOnSelect={false}
        hideSelectedOptions={false}
        formatCreateLabel={formatCreateLabel}
      />
      {question.isOtherSelected && (
        <div className={question.cssClasses.commentArea}>
          <Textarea
            disabled={question.isReadOnly || isDisplayMode}
            onChange={onCommentChange}
            onBlur={onCommentBlur}
            value={comment}
            placeholder={question.commentPlaceHolder}
          />
        </div>
      )}
    </>
  );
};

ReactQuestionFactory.Instance.registerQuestion(
  "sv-multiselect",
  function (props: any) {
    return createElement(MultiselectQuestion, props);
  }
);

RendererFactory.Instance.registerRenderer(
  "tagbox",
  "multiselect",
  "sv-multiselect"
);
